import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BulletList } from 'src/common/components/bullet-list/bullet-list';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { VerticalAlignment } from 'src/common/enumerations/verticalAlignment';
import { MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { store } from 'src/common/redux/configure-store';
import { DeviceNotificationActions } from 'src/common/redux/reducers/device-notification-reducer';
import DeviceMenuModal from './components/device-menu-modal';

const DeviceListShort: React.FC<DeviceListProps> = ({ deviceList }) => {
  const { t } = useTranslation();
  return (
    <>
      <Typography data-testid='bullet-list-title-1'>{t('DeviceAlertSubmessage')}</Typography>
      <Grid
        item
        data-testid='bullet-list-1'
        style={{ display: 'flex', justifyContent: 'center', textAlign: 'left' }}
      >
        <BulletList listItems={deviceList} />
      </Grid>
    </>
  );
};

const DeviceListLong: React.FC<DeviceListProps> = ({ deviceList }) => {
  const { t } = useTranslation();
  return (
    <>
      <Typography data-testid='bullet-list-title-2'>{t('DeviceAlertSubmessage')}</Typography>
      <Grid container direction='row' justifyContent='center'>
        <Grid item data-testid='bullet-list-1' style={{ textAlign: 'left', marginRight: '1vw' }}>
          <BulletList listItems={deviceList.slice(0, 5)} />
        </Grid>
        <Grid item data-testid='bullet-list-2' style={{ textAlign: 'left' }}>
          <BulletList
            listItems={
              deviceList.length <= 9
                ? deviceList.slice(5, deviceList.length)
                : deviceList
                    .slice(5, 9)
                    .concat(t('DeviceAlertMoreMessage', { arg: deviceList.length - 9 }))
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export const showDeviceAlertDialog = (onFinish?: (arg: boolean) => void): void => {
  if (store.getState().deviceNotification.showingDeviceModal) {
    onFinish && onFinish(true);
    return;
  }

  const deviceList: string[] = [];
  const devices = store.getState().deviceStatus.devices;
  if (devices) {
    devices.forEach(device => {
      if (!device.initialized) {
        deviceList.push(device.name);
      }
    });
  }

  store.dispatch(DeviceNotificationActions.setShowingDeviceModal(true));

  const DeviceListComponent = deviceList.length <= 5 ? DeviceListShort : DeviceListLong;

  showEmeraldDialog({
    type: EmeraldDialogTypes.Warning,
    message: 'DeviceAlertMessage',
    subMessage: <DeviceListComponent deviceList={deviceList} />,
    onAccept: () => showDeviceStatusDialog(onFinish),
    onCancel: () => {
      store.dispatch(DeviceNotificationActions.setShowingDeviceModal(false));
      popEmeraldModal();
      onFinish && onFinish(true);
    },
    disableBackdropClick: true,
    size: MessageModalSizeEnum.Large,
    verticalAlignment: VerticalAlignment.Center,
    acceptButtonLabel: 'DeviceAlertAcceptButton',
    closeButtonLabel: 'DeviceAlertIgnoreButton',
    modalId: 'DeviceAlertModal',
  });
};

export const showDeviceStatusDialog = (onFinish?: (arg: boolean) => void): void => {
  const onDialogAccept = () => {
    popEmeraldModal();
    onFinish && onFinish(false);
  };
  popEmeraldModal('DeviceAlertModal');
  const showMenuModal = !!store.getState().deviceStatus.devices.length;
  store.dispatch(DeviceNotificationActions.setShowingDeviceModal(showMenuModal));
  showMenuModal
    ? pushEmeraldModal(<DeviceMenuModal onFinish={onFinish} />, undefined, 'DeviceMenuModal')
    : showEmeraldDialog({
        type: EmeraldDialogTypes.Warning,
        message: 'DeviceNonRegisteredMessage',
        subMessage: 'DeviceNonRegisteredSubmessage',
        onAccept: onDialogAccept,
        disableBackdropClick: true,
        acceptButtonLabel: 'Ok',
        hideCancelButton: true,
      });
};

interface DeviceListProps {
  deviceList: string[];
}
